// UmfragePage.jsx — öffentliche Umfrage nach dem Turnier, /umfrage/:token (V12, 18.10.2026)
//
// Anonym: es geht nur eine zufällige Gerätekennung mit (localStorage), damit
// jedes Gerät einmal antwortet. Kein Name, kein Konto. Das Feld „webseite“
// ist für Menschen unsichtbar (Honigtopf gegen Programme).
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import './UmfragePage.css';

const GERAET_KEY = 'tda-umfrage-geraet';
const erledigtKey = (token) => `tda-umfrage-erledigt-${token}`;

function geraetKennung() {
  try {
    let g = localStorage.getItem(GERAET_KEY);
    if (!g || !/^[A-Za-z0-9_-]{16,64}$/.test(g)) {
      const b = new Uint8Array(18);
      (window.crypto || {}).getRandomValues?.(b);
      g = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
      if (/^0+$/.test(g)) g = `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`.padEnd(20, 'x');
      localStorage.setItem(GERAET_KEY, g);
    }
    return g;
  } catch {
    // Ohne Speicher (privates Fenster): Kennung nur für diesen Aufruf.
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`.padEnd(20, 'x');
  }
}

export default function UmfragePage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState('');
  const [geschlossen, setGeschlossen] = useState(null);
  const [antworten, setAntworten] = useState({});
  const [rolle, setRolle] = useState('');
  const [webseite, setWebseite] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fertig, setFertig] = useState(() => { try { return localStorage.getItem(erledigtKey(token)) === '1'; } catch { return false; } });

  useEffect(() => {
    let ab = false;
    fetch(`/api/umfrage/public/${encodeURIComponent(token)}`)
      .then(async (r) => {
        const x = await r.json().catch(() => ({}));
        if (ab) return;
        if (r.status === 410) { setGeschlossen(x.turnier || {}); return; }
        if (!r.ok || x.success === false) throw new Error(x.error || `Fehler ${r.status}`);
        setD(x);
      })
      .catch((e) => { if (!ab) setFehler(e.message); });
    return () => { ab = true; };
  }, [token]);

  const setze = (id, wert) => setAntworten((a) => ({ ...a, [id]: wert }));
  const beantwortet = Object.values(antworten).some((v) => v !== '' && v != null && String(v).trim() !== '');

  const absenden = async (e) => {
    e.preventDefault();
    setLaeuft(true); setFehler('');
    try {
      const r = await fetch(`/api/umfrage/public/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ antworten, rolle: rolle || null, geraet: geraetKennung(), webseite }),
      });
      const x = await r.json().catch(() => ({}));
      if (r.status === 409 && x.code === 'SCHON_BEANTWORTET') {
        try { localStorage.setItem(erledigtKey(token), '1'); } catch { /* egal */ }
        setFertig(true); return;
      }
      if (!r.ok || x.success === false) throw new Error(x.error || `Fehler ${r.status}`);
      try { localStorage.setItem(erledigtKey(token), '1'); } catch { /* egal */ }
      setFertig(true);
    } catch (err) { setFehler(err.message); } finally { setLaeuft(false); }
  };

  let inhalt;
  if (fertig) {
    inhalt = (
      <div className="umfp-karte umfp-mitte">
        <h1>Danke!</h1>
        <p>Deine Antwort ist angekommen. Sie hilft, das nächste Turnier besser zu machen.</p>
        <Link to="/" className="umfp-link">Zu den Veranstaltungen</Link>
      </div>
    );
  } else if (geschlossen) {
    inhalt = (
      <div className="umfp-karte umfp-mitte">
        <h1>Die Umfrage ist geschlossen</h1>
        {geschlossen.name && <p>{geschlossen.name}{geschlossen.datum ? `, ${geschlossen.datum}` : ''}</p>}
        <Link to="/" className="umfp-link">Zu den Veranstaltungen</Link>
      </div>
    );
  } else if (!d) {
    inhalt = fehler
      ? <div className="umfp-karte umfp-mitte"><h1>Umfrage nicht gefunden</h1><p>{fehler}</p><Link to="/" className="umfp-link">Zu den Veranstaltungen</Link></div>
      : <div className="umfp-karte umfp-mitte"><p>Umfrage wird geladen …</p></div>;
  } else {
    inhalt = (
      <form className="umfp-karte" onSubmit={absenden} noValidate>
        <p className="umfp-ueber">Umfrage nach dem Turnier</p>
        <h1>{d.turnier.name}</h1>
        {d.turnier.datum && <p className="umfp-leise">{d.turnier.datum}</p>}
        <p className="umfp-leise">Anonym, etwa eine Minute. Jede Frage ist freiwillig. 1 = schlecht, 5 = sehr gut.</p>

        <fieldset className="umfp-frage">
          <legend>Ich war dabei als</legend>
          <select value={rolle} onChange={(e) => setRolle(e.target.value)} className="umfp-auswahl">
            <option value="">keine Angabe</option>
            {Object.entries(d.rollen || {}).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select>
        </fieldset>

        {d.fragen.map((f) => (
          <fieldset key={f.id} className="umfp-frage">
            <legend>{f.text}</legend>
            {f.typ === 'skala' ? (
              <div className="umfp-skala" role="radiogroup" aria-label={f.text}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <label key={n} className={`umfp-stufe${antworten[f.id] === n ? ' gewaehlt' : ''}`}>
                    <input type="radio" name={f.id} value={n} checked={antworten[f.id] === n} onChange={() => setze(f.id, n)} />
                    <span>{n}</span>
                  </label>
                ))}
              </div>
            ) : (
              <textarea className="umfp-text" rows={4} maxLength={2000} value={antworten[f.id] || ''}
                onChange={(e) => setze(f.id, e.target.value)} aria-label={f.text} />
            )}
          </fieldset>
        ))}

        {/* Honigtopf: für Menschen unsichtbar, Programme füllen es aus */}
        <div className="umfp-versteckt" aria-hidden="true">
          <label>Webseite <input tabIndex={-1} autoComplete="off" value={webseite} onChange={(e) => setWebseite(e.target.value)} /></label>
        </div>

        {fehler && <p className="umfp-fehler" role="alert">{fehler}</p>}
        <button type="submit" className="umfp-senden" disabled={laeuft || !beantwortet}>
          {laeuft ? 'wird gesendet …' : 'Antwort absenden'}
        </button>
        {!beantwortet && <p className="umfp-leise">Bitte mindestens eine Frage beantworten.</p>}
      </form>
    );
  }

  return <div className="umfp-seite">{inhalt}</div>;
}
